import React, { useState } from 'react';
import { 
  Pill, 
  Search, 
  Filter, 
  HelpCircle,
  FileText,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import './Vademecum.css';

const Vademecum = () => {
  const [searchType, setSearchType] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotifications();

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      addNotification('Por favor ingrese un término de búsqueda', 'info');
      return;
    }
    setLoading(true);
    try {
      let query = supabase.from('vademecum').select('*');
      
      const terms = searchTerm.trim().split(/\s+/);
      const andQuery = terms.map(t => `${searchType}.ilike.%${t}%`).join(',');
      query = query.or(`and(${andQuery})`).limit(20);

      const { data, error } = await query;

      if (error) throw error;
      setResults(data || []);
      if (data.length === 0) {
        addNotification('No se encontraron medicamentos', 'info');
      }
    } catch (err) {
      addNotification(`Error al buscar: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vademecum-page">
      <div className="vademecum-header">
        <div className="vade-title-main">
          <Pill size={32} className="vade-icon-primary" />
          <h1>Vademecum Argentina</h1>
        </div>
        <p className="vade-subtitle">Buscador oficial de medicamentos y monodrogas.</p>
      </div>

      <div className="vademecum-search card">
        <div className="search-grid">
          <div className="search-field">
            <label>Tipo de Búsqueda</label>
            <div className="select-wrapper">
              <select value={searchType} onChange={(e) => setSearchType(e.target.value)}>
                <option value="name">Nombre Comercial</option>
                <option value="active_ingredient">Monodroga / Droga</option>
                <option value="category">Categoría</option>
              </select>
              <ChevronDown size={16} className="select-icon" />
            </div>
          </div>

          <div className="search-field grow">
            <label>Término de Búsqueda</label>
            <div className="input-with-icon">
              <Search size={18} className="input-icon" />
              <input 
                type="text" 
                placeholder="Ej: Ibuprofeno, Amoxicilina, Analgésicos..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          </div>

          <div className="search-button-container">
            <button className="btn-vade-search" onClick={handleSearch} disabled={loading}>
              {loading ? <RefreshCw size={18} className="animate-spin" /> : 'BUSCAR'}
            </button>
          </div>
        </div>
      </div>

      <div className="vademecum-table-container card">
        <table className="vademecum-table">
          <thead>
            <tr>
              <th>Nombre Comercial</th>
              <th>Monodroga (Acción)</th>
              <th>Descripción</th>
              <th>Categoría</th>
            </tr>
          </thead>
          <tbody>
            {results.map(item => (
              <tr key={item.id}>
                <td className="font-bold text-blue">{item.name}</td>
                <td className="drug-cell">{item.active_ingredient}</td>
                <td className="text-muted">{item.description}</td>
                <td>
                  <span className="category-tag">{item.category}</span>
                </td>
              </tr>
            ))}
            {results.length === 0 && !loading && (
              <tr>
                <td colSpan="4" className="empty-state">Inicie una búsqueda para ver resultados</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="vade-footer-info">
        <div className="help-badge">
          <HelpCircle size={16} />
          <span>¿No encuentra un medicamento? Contáctenos para actualizar la base.</span>
        </div>
      </div>

      <button className="btn-floating-help">
        <FileText size={20} />
      </button>
    </div>
  );
};

export default Vademecum;
